import { z } from 'zod';
import { IsoTimestamp, UserId } from './common';

/**
 * Loose parsed-metadata bag. The shape is whatever the current LLM
 * extractor produces — historically a `personal` / `techExperience` /
 * record-shaped `summary` blob; today (post-2026-06-17 parse-resume
 * rewrite) a flat object with `fullName`, `summary` as a STRING, full
 * `workHistory` / `education` arrays, etc. Both old fixtures and new
 * outputs flow through `/v1/resume` to the mobile / desktop clients, so
 * the schema deliberately validates only "is this a JSON object?" — any
 * stricter shape would silently break the mobile Resume screen (Zod
 * throws → `getResumeMeta()` returns null → "Pick a PDF" empty state
 * shown over a perfectly good upload). Field-level shape is enforced
 * downstream by `syncParsedIntoProfile` and the strict `CandidateProfile`
 * schema, which is the right place for that — `parsedMetadata` is a
 * cache of the raw extract, not the canonical user-facing model.
 */
export const ResumeMetadata = z.record(z.string(), z.unknown());

export type ResumeMetadata = z.infer<typeof ResumeMetadata>;

export const Resume = z.object({
  userId: UserId,
  version: z.number().int().nonnegative(),
  s3Key: z.string().min(1),
  contentType: z.string().min(1),
  sizeBytes: z.number().int().nonnegative(),
  parsedMetadata: ResumeMetadata.optional(),
  parsedAt: IsoTimestamp.optional(),
  uploadedAt: IsoTimestamp,
});

export type Resume = z.infer<typeof Resume>;

export const CreateResumeUploadRequest = z.object({
  contentType: z.literal('application/pdf'),
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(20 * 1024 * 1024),
});

export const CreateResumeUploadResponse = z.object({
  uploadUrl: z.string().url(),
  s3Key: z.string(),
  version: z.number().int(),
  expiresAt: IsoTimestamp,
});
